/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — TexteATrou UI (Track J.3a — 2026-09-14)
 * ----------------------------------------------------------------------------
 * Source-of-truth : shared/mte-product/03-skill-taxonomy.md
 * Owner : Atelier (Track J.3a)
 *
 * Archetype : "texte-a-trou" (cloze input — 1-2 champs texte)
 * Props (canonical, see lib/banque-types.ts):
 *   - item          : BanqueItemTexteATrou
 *   - onAnswer      : (correct: boolean, payload?: unknown) => void
 *   - disabled?     : boolean
 *   - showFeedback? : 'idle' | 'correct' | 'incorrect'
 * ========================================================================== */

import React, { useState, useMemo } from 'react';
import type {
  BanqueItemTexteATrou,
  Trou,
} from '../../../../lib/banque-types';
import { isAnswerCorrect } from '../../../../lib/banque-types';

export interface TexteATrouProps {
  item: BanqueItemTexteATrou;
  onAnswer: (correct: boolean, payload?: unknown) => void;
  disabled?: boolean;
  showFeedback?: 'idle' | 'correct' | 'incorrect';
}

export function TexteATrou({
  item,
  onAnswer,
  disabled = false,
  showFeedback = 'idle',
}: TexteATrouProps): JSX.Element {
  const initialValues = useMemo<Record<string, string>>(
    () => Object.fromEntries(item.trous.map((t) => [t.id, ''])),
    [item],
  );
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const allFilled = item.trous.every((t) => values[t.id].trim().length > 0);

  const handleChange = (trouId: string, value: string): void => {
    setValues((prev) => ({ ...prev, [trouId]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!allFilled || submitted || disabled) return;
    setSubmitted(true);
    const perTrou: Record<string, boolean> = {};
    let allCorrect = true;
    for (const t of item.trous) {
      const ok = isAnswerCorrect(values[t.id], t.bonneReponse, t.tolerance ?? 0);
      perTrou[t.id] = ok;
      if (!ok) allCorrect = false;
    }
    onAnswer(allCorrect, { perTrou, values });
  };

  const renderSegment = (segment: string, idx: number): React.ReactNode => {
    const trouMatch = segment.match(/^\{T(\d+)\}$/);
    if (trouMatch) {
      const trouId = `T${trouMatch[1]}`;
      const trou: Trou | undefined = item.trous.find((t) => t.id === trouId);
      if (!trou) return null;
      const isCorrectHere = submitted && values[trouId] && perTrouResult[trouId];
      const isIncorrectHere = submitted && values[trouId] && !perTrouResult[trouId];
      return (
        <input
          key={`trou-${idx}-${trouId}`}
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={disabled || submitted}
          placeholder={trou.placeholder ?? '…'}
          aria-label={`Trou ${trouId}`}
          data-testid={`trou-${trouId}`}
          data-feedback={
            submitted
              ? isCorrectHere
                ? 'correct'
                : isIncorrectHere
                ? 'incorrect'
                : 'idle'
              : 'idle'
          }
          className="texte-a-trou-input"
          value={values[trouId]}
          onChange={(e) => handleChange(trouId, e.target.value)}
        />
      );
    }
    return <React.Fragment key={`seg-${idx}`}>{segment}</React.Fragment>;
  };

  // pre-compute per-trou result for feedback class (after submit only)
  const perTrouResult: Record<string, boolean> = {};
  if (submitted) {
    for (const t of item.trous) {
      perTrouResult[t.id] = isAnswerCorrect(
        values[t.id],
        t.bonneReponse,
        t.tolerance ?? 0,
      );
    }
  }

  const feedbackClass = submitted
    ? showFeedback !== 'idle'
      ? showFeedback
      : Object.values(perTrouResult).every(Boolean)
      ? 'correct'
      : 'incorrect'
    : 'idle';

  return (
    <form
      className="texte-a-trou"
      data-testid="texte-a-trou"
      data-archetype="texte-a-trou"
      data-item-id={item.id}
      data-feedback={feedbackClass}
      onSubmit={handleSubmit}
    >
      <p className="texte-a-trou-enonce" aria-label="Énoncé">
        {item.enonceSegments.map(renderSegment)}
      </p>
      {item.consigne && (
        <p className="texte-a-trou-consigne">{item.consigne}</p>
      )}
      <button
        type="submit"
        className="texte-a-trou-submit"
        data-action="soumettre"
        data-testid="texte-a-trou-submit"
        disabled={!allFilled || submitted || disabled}
      >
        Valider
      </button>
      {submitted && (
        <p className="texte-a-trou-feedback" data-testid="texte-a-trou-feedback">
          {Object.values(perTrouResult).every(Boolean)
            ? 'Bravo, tu as tout complété !'
            : 'Quelques réponses sont à revoir.'}
        </p>
      )}
    </form>
  );
}

export default TexteATrou;